import { useState, type FormEvent } from "react";

import { errorMessage, isBadCredentialsError, isUnreachableError } from "@/lib/errors";
import { PB_DISPLAY_URL } from "@/lib/pb";
// Imported by path rather than through `@/ui`: the sign-in screen renders
// before the overlay layer is mounted, and there is no reason for a signed-out
// window to pull in dialogs, toasts and a calendar it cannot use.
import { Alert } from "@/ui/Alert";
import { Button } from "@/ui/Button";
import { enterProps, typewriterProps } from "@/ui/motion";
import { Panel } from "@/ui/Panel";
import { TextField } from "@/ui/TextField";
import { useSession } from "./SessionContext";
import { SkyBackdrop } from "@/shell/SkyBackdrop";
import "@/ui/ps1/ps1.css";
import { DEV_LOGIN } from "./devLogin";
import koboLogo from "@/assets/logo-b.webp";

/**
 * Sign-in for a Kobo account.
 *
 * There is no sign-up and no password reset here on purpose: accounts are created
 * on the server (`pb/deploy/kobo-add-user`), so extra doors would only be surface area.
 */
export function LoginScreen() {
  const { signIn } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await submit(email, password);
  }

  async function submit(withEmail: string, withPassword: string) {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await signIn(withEmail, withPassword);
    } catch (caught) {
      if (isUnreachableError(caught)) {
        setError(`Cannot reach the Kobo server at ${PB_DISPLAY_URL}.`);
      } else if (isBadCredentialsError(caught)) {
        setError("That email and password do not match.");
      } else {
        setError(errorMessage(caught));
      }
      setSubmitting(false);
    }
  }

  return (
    <div
      {...enterProps("content")}
      className="ps1-backdrop relative isolate flex min-h-full items-center justify-center p-4"
    >
      <SkyBackdrop />
      {/* A PS1 "memory card" screen: the purple title pill and gold meta line
          the page toolbars use, then one orange-framed box holding the form. */}
      <div className="flex w-full max-w-md flex-col gap-4">
        <header className="flex flex-col items-start gap-2">
          <h1><img src={koboLogo} alt="Kobo" className="block h-16 w-auto" /></h1>
          <p
            {...typewriterProps("Sign in to pick your day back up.")}
            className="ps1-meta"
          >
            Sign in to pick your day back up.
          </p>
        </header>

        <Panel tone="hairline" padding="none" aria-labelledby="login-box-head">
          <h2
            id="login-box-head"
            className="border-b border-ps1-line px-3 py-1.5 text-section font-normal text-ps1-gold"
          >
            Sign in
          </h2>
          <form className="flex flex-col gap-pad p-4" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />

            <TextField
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />

            {error ? <Alert>{error}</Alert> : null}

            <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
              Sign in
            </Button>

            {DEV_LOGIN ? (
              <Button
                type="button"
                variant="secondary"
                size="lg"
                fullWidth
                disabled={submitting}
                onClick={() => void submit(DEV_LOGIN!.email, DEV_LOGIN!.password)}
              >
                Quick login (localhost)
              </Button>
            ) : null}
          </form>
          <p className="border-t border-ps1-line px-3 py-2 text-caption text-subtle">
            Connected to <span className="numeric">{PB_DISPLAY_URL}</span>
          </p>
        </Panel>
      </div>
    </div>
  );
}
